import { BoardFragmentFragment } from '@apps/www/src/__generated__/graphql';
import SVCopyBoardPublicURLContainer from '@apps/www/src/www/containers/SVCopyBoardPublicURLContainer';
import SVEditBoardFieldContainer from '@apps/www/src/www/containers/SVEditBoardFieldContainer';
import SVPrintActionContainer from '@apps/www/src/www/containers/SVPrintActionContainer';
import SVShareBoardContainer from '@apps/www/src/www/containers/SVShareBoardContainer';
import SVDeleteBoardModal from '@apps/www/src/www/modals/SVDeleteBoardModal';
import SVEditBoardModal from '@apps/www/src/www/modals/SVEditBoardModal';
import SVDropdown from '@pkgs/shared-client/components/SVDropdown';
import SVDropdownContent from '@pkgs/shared-client/components/SVDropdownContent';
import SVIconButton, { SVIconButtonSIZES } from '@pkgs/shared-client/components/SVIconButton';
import SVModal from '@pkgs/shared-client/components/SVModal';
import cn from '@pkgs/shared-client/helpers/cn';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import IconChevronDownSVG from '@pkgs/shared-client/img/icon-chevron-down-inlined.svg';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import BoardUserRole from '@pkgs/shared/enums/BoardUserRole';
import boardUserRoleHasBoardUserRolePrivileges from '@pkgs/shared/helpers/boardUserRoleHasBoardUserRolePrivileges';

const _UserBoardDropdown = ({
	hasViewerPrivileges,
	hasAdminPrivileges,
	hasEditorPrivileges,
	board,
	handleEdit,
	handleDelete,
	triggerIcon,
	iconClassName,
	onGrid,
	onOpenChange,
	disableHover,
}: {
	hasViewerPrivileges: boolean;
	hasAdminPrivileges: boolean;
	hasEditorPrivileges: boolean;
	board: BoardFragmentFragment;
	handleEdit: () => void;
	handleDelete: () => void;
	triggerIcon: SVGComponent;
	iconClassName?: string;
	onGrid?: string;
	onOpenChange?: (isOpen: boolean) => void;
	disableHover?: boolean;
}) => {
	return hasViewerPrivileges ? (
		<div className="flex items-center" title="More options">
			<SVDropdown
				triggerType={SVDropdown.TRIGGER_TYPES.CLICK}
				contentOffset="h-3"
				keepOpenOnClick
				positionStrategy={SVDropdown.POSITION_STRATEGIES.FIXED}
				renderTrigger={({ isOpen, ...props }) => {
					onOpenChange?.(isOpen);
					return (
						<SVIconButton
							src={triggerIcon}
							showCircle={false}
							label="More options"
							size={SVIconButtonSIZES.SMALL}
							className={cn('text-gray-300', iconClassName)}
							onGrid={cn('h-7 min-h-7 w-7 min-w-7', onGrid)}
							disableHover={disableHover}
							{...props}
						/>
					);
				}}
				renderContent={() => (
					<SVDropdownContent.Links>
						{hasAdminPrivileges && (
							<>
								<SVDropdownContent.Links.Item
									className="edit"
									onClick={handleEdit}
									title="Edit board"
								>
									Edit
								</SVDropdownContent.Links.Item>

								<SVEditBoardFieldContainer
									board={board}
									field="isPrivate"
									hasAdminPrivileges={hasAdminPrivileges}
									title={board.isPrivate ? 'Make public' : 'Make private'}
								>
									Private board
								</SVEditBoardFieldContainer>

								<SVEditBoardFieldContainer
									board={board}
									field="isHidden"
									hasAdminPrivileges={hasAdminPrivileges}
									title="Won't show up on your profile and feed."
								>
									<div className="mr-4">
										<span>Hide from feed</span>
									</div>
								</SVEditBoardFieldContainer>

								{board.isPrivate && (
									<SVCopyBoardPublicURLContainer
										boardID={board._id}
										boardPublicURL={board.publicURL}
									/>
								)}
							</>
						)}

						{!board.isPrivate && <SVShareBoardContainer board={board} />}

						{!hasAdminPrivileges && (
							<SVDropdownContent.Links.ItemLink
								to={`/api/boards/${board._id}/leave/`}
								title="Leave board"
							>
								Leave board
							</SVDropdownContent.Links.ItemLink>
						)}

						{(hasAdminPrivileges || hasEditorPrivileges) && (
							<SVPrintActionContainer
								Component={SVDropdownContent.Links.Item}
								title="Print board"
								url={board.url}
							>
								Print
							</SVPrintActionContainer>
						)}

						{hasAdminPrivileges && (
							<SVDropdownContent.Links.Item
								title="Delete board"
								onClick={handleDelete}
							>
								Delete
							</SVDropdownContent.Links.Item>
						)}
					</SVDropdownContent.Links>
				)}
			/>
		</div>
	) : null;
};

const _TeamBoardDropdown = ({
	hasAdminPrivileges,
	handleEdit,
	board,
	handleDelete,
	triggerIcon,
	iconClassName,
	onGrid,
	onOpenChange,
	disableHover = false,
}: {
	hasAdminPrivileges: boolean;
	handleEdit: () => void;
	board: BoardFragmentFragment;
	handleDelete: () => void;
	triggerIcon: SVGComponent;
	iconClassName?: string;
	onGrid?: string;
	onOpenChange?: (isOpen: boolean) => void;
	disableHover?: boolean;
}) => {
	return hasAdminPrivileges ? (
		<div className="flex items-center" title="More options">
			<SVDropdown
				triggerType={SVDropdown.TRIGGER_TYPES.CLICK}
				contentOffset="h-3"
				positionStrategy={SVDropdown.POSITION_STRATEGIES.FIXED}
				keepOpenOnClick
				renderTrigger={({ isOpen, ...props }) => {
					onOpenChange?.(isOpen);
					return (
						<SVIconButton
							src={triggerIcon}
							showCircle={false}
							disableHover={disableHover}
							label="More options"
							size={SVIconButtonSIZES.SMALL}
							className={cn('text-gray-300', iconClassName)}
							onGrid={cn('h-7 min-h-7 w-7 min-w-7', onGrid)}
							{...props}
						/>
					);
				}}
				renderContent={() => (
					<SVDropdownContent.Links>
						<SVDropdownContent.Links.Item
							className="edit"
							onClick={handleEdit}
							title="Edit board"
						>
							Edit
						</SVDropdownContent.Links.Item>
						{board.isPrivate && (
							<SVCopyBoardPublicURLContainer
								boardID={board._id}
								boardPublicURL={board.publicURL}
							/>
						)}
						<SVDropdownContent.Links.Item title="Delete board" onClick={handleDelete}>
							Delete
						</SVDropdownContent.Links.Item>
					</SVDropdownContent.Links>
				)}
			/>
		</div>
	) : null;
};

const SVBoardOptionsContainer = ({
	type,
	board,
	role,
	triggerIcon = IconChevronDownSVG,
	iconClassName,
	onGrid,
	onOpenChange,
	disableHover,
}: {
	type: ValueOf<typeof BoardOwnershipType>;
	board: BoardFragmentFragment;
	role: ValueOf<typeof BoardUserRole>;
	triggerIcon?: SVGComponent;
	iconClassName?: string;
	onGrid?: string;
	onOpenChange?: (isOpen: boolean) => void;
	disableHover?: boolean;
}) => {
	const hasAdminPrivileges = boardUserRoleHasBoardUserRolePrivileges(role, BoardUserRole.ADMIN);
	const hasEditorPrivileges = boardUserRoleHasBoardUserRolePrivileges(role, BoardUserRole.EDITOR);
	const hasViewerPrivileges = boardUserRoleHasBoardUserRolePrivileges(role, BoardUserRole.VIEWER);

	const handleEdit = useEventCallback(() => {
		if (!board) {
			return;
		}

		SVModal.open(SVEditBoardModal, {
			board,
		});
	});

	const handleDelete = useEventCallback(() => {
		if (!board) {
			return;
		}

		SVModal.open(SVDeleteBoardModal, {
			_id: board._id,
			name: board.name,
			ownershipType: board.ownershipType,
		});
	});

	return type === BoardOwnershipType.USER ? (
		<_UserBoardDropdown
			hasViewerPrivileges={hasViewerPrivileges}
			hasAdminPrivileges={hasAdminPrivileges}
			hasEditorPrivileges={hasEditorPrivileges}
			board={board}
			handleEdit={handleEdit}
			handleDelete={handleDelete}
			triggerIcon={triggerIcon}
			iconClassName={iconClassName}
			onGrid={onGrid}
			onOpenChange={onOpenChange}
			disableHover={disableHover}
		/>
	) : (
		<_TeamBoardDropdown
			hasAdminPrivileges={hasAdminPrivileges}
			board={board}
			handleEdit={handleEdit}
			handleDelete={handleDelete}
			triggerIcon={triggerIcon}
			iconClassName={iconClassName}
			onGrid={onGrid}
			onOpenChange={onOpenChange}
			disableHover={disableHover}
		/>
	);
};

export default SVBoardOptionsContainer;
